import axios from 'axios';
import Pager from '@/helpers/Pager';
import merge from 'lodash/merge';
import { Pagination, Resource, Statement, Option } from '@/helpers/Interfaces';
import { FormModel as PropertyFormModel } from '../../properties/services/PropertiesService';
import { $t } from '@/plugins/localization';

export default class OwnersService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(pager: Pager, filter: FilterModel): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/renters/owners', {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(publicId: string): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/renters/owners/${publicId}`)).data;
    }

    /**
     * @param model FormModel
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async create(model: FormModel): Promise<Resource<FormModel>>
    {
        return (await axios.post<Resource<FormModel>>(`admin/renters/owners`, model)).data;
    }

    /**
     * @param publicId string
     *
     * @param model FormModel
     *
     * @returns Promise<Statement>
     */
    public static async update(publicId: string, model: FormModel): Promise<Statement>
    {
        return (await axios.put<Statement>(`admin/renters/owners/${publicId}`, model)).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Statement>
     */
    public static async remove(publicId: string): Promise<Statement>
    {
        return (await axios.delete<Statement>(`admin/renters/owners/${publicId}`)).data;
    }

    /**
     * @returns Promise<Resource<FormModel>>
     */
    public static async getProperties(): Promise<PropertyFormModel[]>
    {
        return (await axios.get<PropertyFormModel[]>(`admin/renters/properties/byOwner?withoutOwner=true`)).data;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 10): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>('renters/owners/autocomplete', {
            params: {
                search: search,
                limit: limit
            }
        })).data;
    }

    /**
     * @param publicId string
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetchAutocompleteModel(publicId: string): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`renters/owners/autocomplete/${publicId}`)).data;
    }
}

export interface FilterModel{
    showNonActive: boolean;
    propertyPublicId: string;
    name: string;
}

export interface ListItemModel{
    id: number;
    publicId: string;
    ownerFacePicture: any;
    givenName: string;
    surname: string;
    companyName: string;
    CompanyAddress: string;
    companyNip: string;
    companyRegion: string;
}

export interface UserPropertyModel{
    id: number;
}

export interface FormModel{
    publicId: string;
    properties: Array<UserPropertyModel>;
    givenName: string;
    surname: string;
    phoneNumber: string;
    email: string;
    address: string;
    identityCardNumber: string;
    bankName: string;
    language: string;
    bicSwift: string;
    iban: string;
    companyName: string;
    companyAddress: string;
    companyNip: string;
    companyRegon: string;
}

enum LanguageEnum {
    Polish = 'Polish',
    English = 'English',
    German = 'German',
}

const LanguageOptions = (): Option[] => ([
    { value: LanguageEnum.Polish, text: $t('[[[Polski]]]') },
    { value: LanguageEnum.English, text: $t('[[[Angielski]]]') },
    { value: LanguageEnum.German, text: $t('[[[Niemiecki]]]') }
]);

const LanguageName = (value: LanguageEnum): string => LanguageOptions().find(l => l.value === value)?.text || LanguageEnum[value];

export {
    LanguageEnum,
    LanguageOptions,
    LanguageName
};
